import React from 'react';
import T from 'prop-types';
import cn from 'classnames';
import {useDispatch} from 'react-redux';
import {ButtonOutline} from 'components/buttons';
import {
  OPEN_CONFIRMATION_MODAL,
  OPEN_DISABLE_MODAL
} from 'store/locationSettings/action';
import {
  SAUCE_CHANNEL_NAME,
  getProviderDisplayName,
  getProviderIcon,
  providerNamesMatch
} from 'helpers/constants/storeSettings';
import {
  areAllChannelsPaused,
  getChannelsStatusText,
  getPauseSettings,
  isProviderPaused
} from 'helpers/functionUtils/storeSettings';
import {
  OrderingChannelCard,
  OrderingChannelDivider,
  OrderingChannelIcon,
  OrderingChannelName,
  OrderingChannelsGrid,
  OrderingChannelsHeader,
  OrderingChannelsStatus,
  OrderingChannelsTitle,
  OrderingChannelStatusDot,
  OrderingChannelStatusText,
  OrderingChannelSubtitle
} from 'routes/App/settings/styles';

const ChannelCard = ({name, subtitle, paused, onClick}) => {
  const icon = getProviderIcon(name);
  const displayName = getProviderDisplayName(name);

  return (
    <OrderingChannelCard
      type='button'
      className={cn({paused})}
      onClick={onClick}
    >
      <OrderingChannelStatusDot className={cn({paused})} />
      {icon && <OrderingChannelIcon src={icon} alt={displayName} />}
      <OrderingChannelName>{displayName}</OrderingChannelName>
      {subtitle && (
        <OrderingChannelSubtitle>{subtitle}</OrderingChannelSubtitle>
      )}
      <OrderingChannelStatusText className={cn({paused})}>
        {paused ? 'Paused' : 'Active'}
      </OrderingChannelStatusText>
    </OrderingChannelCard>
  );
};

ChannelCard.propTypes = {
  name: T.string.isRequired,
  subtitle: T.string,
  paused: T.bool,
  onClick: T.func.isRequired
};

ChannelCard.defaultProps = {
  subtitle: '',
  paused: false
};

const OrderingChannels = ({
  closedManually,
  externalPlatforms,
  externalOrderProviders
}) => {
  const dispatch = useDispatch();
  const pauseSettings = getPauseSettings(externalOrderProviders);
  const allPaused = areAllChannelsPaused({
    closedManually,
    externalPlatforms,
    pauseSettings
  });

  // Sauce = ClosedManually. Externals = PauseSettings only (ClosedManually does not pause them in UI).
  const isChannelPaused = (providerName) => {
    if (providerNamesMatch(providerName, SAUCE_CHANNEL_NAME)) {
      return !!closedManually;
    }
    return isProviderPaused(pauseSettings, providerName);
  };

  const onChannelClick = (providerName) => {
    const isSauce = providerNamesMatch(providerName, SAUCE_CHANNEL_NAME);

    if (isSauce) {
      if (closedManually) {
        dispatch(
          OPEN_CONFIRMATION_MODAL({
            open: true,
            entityName: 'Store'
          })
        );
        return;
      }

      dispatch(
        OPEN_DISABLE_MODAL({
          open: true,
          entityName: 'Store'
        })
      );
      return;
    }

    if (isProviderPaused(pauseSettings, providerName)) {
      dispatch(
        OPEN_CONFIRMATION_MODAL({
          open: true,
          entityName: 'Store',
          providerName
        })
      );
      return;
    }

    dispatch(
      OPEN_DISABLE_MODAL({
        open: true,
        entityName: 'Store',
        providerName
      })
    );
  };

  const onPauseAllClick = () => {
    if (allPaused) {
      dispatch(
        OPEN_CONFIRMATION_MODAL({
          open: true,
          entityName: 'Store',
          pauseAllChannels: true
        })
      );
      return;
    }

    dispatch(
      OPEN_DISABLE_MODAL({
        open: true,
        entityName: 'Store',
        pauseAllChannels: true
      })
    );
  };

  const statusText = getChannelsStatusText({
    closedManually,
    externalPlatforms,
    pauseSettings
  });

  return (
    <div>
      <OrderingChannelsHeader>
        <div>
          <OrderingChannelsTitle>Ordering channels</OrderingChannelsTitle>
          <OrderingChannelsStatus>{statusText}</OrderingChannelsStatus>
        </div>
        <ButtonOutline
          className='ButtonSmall'
          type='button'
          onClick={onPauseAllClick}
        >
          <span>
            {allPaused ? 'Unpause all channels' : 'Pause all channels'}
          </span>
        </ButtonOutline>
      </OrderingChannelsHeader>

      {!!externalPlatforms?.length && (
        <OrderingChannelsGrid>
          {externalPlatforms.map((platform) => {
            const name = platform.platformName || platform;
            return (
              <ChannelCard
                key={name}
                name={name}
                paused={isChannelPaused(name)}
                onClick={() => onChannelClick(name)}
              />
            );
          })}
        </OrderingChannelsGrid>
      )}

      <OrderingChannelDivider>Your own channel</OrderingChannelDivider>

      <OrderingChannelsGrid>
        <ChannelCard
          name={SAUCE_CHANNEL_NAME}
          subtitle='Direct Ordering'
          paused={isChannelPaused(SAUCE_CHANNEL_NAME)}
          onClick={() => onChannelClick(SAUCE_CHANNEL_NAME)}
        />
      </OrderingChannelsGrid>
    </div>
  );
};

OrderingChannels.propTypes = {
  closedManually: T.bool,
  externalPlatforms: T.array,
  externalOrderProviders: T.object
};

OrderingChannels.defaultProps = {
  closedManually: false,
  externalPlatforms: [],
  externalOrderProviders: null
};

export default OrderingChannels;
